import { FunctionComponent } from 'react';
import { Box, Container, Typography, Grid, TextField, Button, Paper, Snackbar, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useNavigate } from 'react-router-dom';
import { useQuestionForm } from './common/useQuestionForm';

const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  minHeight: '100%',
}));

const MapContainer = styled(Box)({
  width: '100%',
  height: '400px',
  marginBottom: '2rem',
});

const ContactSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const FormSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const ContactInfo = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  // Removed & > * selector which might be causing issues or not needed
}));

const HoursList = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
}));

const HoursItem = styled('li')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
}));

const Visitors: FunctionComponent = () => {
  const navigate = useNavigate();
  const { formData, handleChange, handleSubmit, submitting, snackbar, handleCloseSnackbar } = useQuestionForm();

  return (
    <PageContainer>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom align="center" sx={{ my: 6 }}>
          Visit Us
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: 4, maxWidth: '800px', mx: 'auto' }}>
          The Church of God Cincinnati is a Christian, seventh-day Sabbath and Holy Day keeping congregation. We seek to spread the gospel, teach biblical principles and values and support and encourage our brethren. We welcome in-person and online visitors to worship with us each Sabbath at 2:00 PM
        </Typography>

        <MapContainer>
          <iframe
            title="Church Location"
            width="100%"
            height="100%"
            frameBorder="0"
            style={{ border: 0 }}
            src="https://www.google.com/maps?q=9191+Daly+Rd,+Cincinnati,+OH&output=embed"
            allowFullScreen
          ></iframe>
        </MapContainer>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <ContactSection>
              <Typography variant="h5" gutterBottom>
                Contact Information
              </Typography>
              <ContactInfo>
                <Typography variant="h6" gutterBottom>
                  Address
                </Typography>
                <Typography variant="body1">
                  9191 Daly Rd.<br />
                  Cincinnati Ohio 45321
                </Typography>
              </ContactInfo>

              <ContactInfo>
                <Typography variant="h6" gutterBottom>
                  Contact
                </Typography>
                <Typography variant="body1">
                  Phone: (513) 755-0040
                </Typography>
              </ContactInfo>

              <ContactInfo>
                <Typography variant="h6" gutterBottom>
                  Service Times
                </Typography>
                <HoursList>
                  <HoursItem>
                    <Typography variant="body1">Saturdays</Typography>
                    <Typography variant="body1">2:00 PM</Typography>
                  </HoursItem>
                </HoursList>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate('/streaming')}
                  sx={{ mt: 2 }}
                >
                  Watch Live
                </Button>
              </ContactInfo>
            </ContactSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormSection>
              <Typography variant="h5" gutterBottom>
                Have Questions?
              </Typography>
              <Typography variant="body1" paragraph>
                We'd love to hear from you! Fill out the form below and we'll get back to you as soon as possible.
              </Typography>
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      multiline
                      rows={4}
                      label="Message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={submitting}
                    >
                      {submitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </FormSection>
          </Grid>
        </Grid>
      </Container>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default Visitors; 